import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  GraduationCap, 
  BookOpen,
  CheckCircle,
  ArrowRight,
  Clock,
  Users,
  Award,
  Lightbulb,
  Target
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageBanner } from '../components/layout/PageBanner';

const Careers = () => {
  const { t, language } = useLanguage();

  const programs = [
    {
      icon: GraduationCap,
      title: t('careers.internTitle'),
      description: t('careers.internDesc'),
      duration: language === 'hi' ? '2-6 महीने' : '2-6 months',
      features: language === 'hi'
        ? ['प्रोजेक्ट-आधारित शिक्षा', 'विशेषज्ञ मार्गदर्शन', 'प्रमाण पत्र']
        : ['Project-based learning', 'Expert mentorship', 'Certificate']
    },
    {
      icon: Briefcase,
      title: t('careers.trainingTitle'),
      description: t('careers.trainingDesc'),
      duration: language === 'hi' ? '3-12 महीने' : '3-12 months',
      features: language === 'hi'
        ? ['प्रशिक्षण + कार्य अनुभव', 'वास्तविक परियोजनाएं', 'करियर अवसर']
        : ['Training + Work experience', 'Real projects', 'Career opportunities']
    },
    {
      icon: BookOpen,
      title: t('careers.skillTitle'),
      description: t('careers.skillDesc'),
      duration: language === 'hi' ? '4-16 सप्ताह' : '4-16 weeks',
      features: language === 'hi'
        ? ['उद्योग-प्रासंगिक कौशल', 'हैंड्स-ऑन प्रशिक्षण', 'प्लेसमेंट सहायता']
        : ['Industry-relevant skills', 'Hands-on training', 'Placement support']
    }
  ];

  const benefits = [
    { icon: Lightbulb, text: t('careers.benefit1') },
    { icon: Users, text: t('careers.benefit2') },
    { icon: Target, text: t('careers.benefit3') },
    { icon: Award, text: t('careers.benefit4') },
    { icon: Briefcase, text: t('careers.benefit5') }
  ];

  return (
    <main id="main-content">
      <PageBanner 
        title={t('careers.pageTitle')} 
        breadcrumb={t('careers.breadcrumb')} 
      />

      {/* Introduction */}
      <section className="gov-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'hsl(var(--muted-foreground))',
                textAlign: 'center',
                lineHeight: 1.7
              }}>
                {t('careers.intro')}
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Programs */}
      <section className="gov-section gov-section-alt" style={{ paddingTop: 0 }}>
        <Container>
          <Row className="g-4">
            {programs.map((program, index) => (
              <Col key={index} lg={4}>
                <Card className="gov-card h-100">
                  <Card.Body className="d-flex flex-column p-4">
                    <div 
                      style={{
                        width: '56px',
                        height: '56px',
                        background: 'var(--gradient-accent-subtle)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.25rem'
                      }}
                    >
                      <program.icon size={28} style={{ color: 'hsl(var(--primary))' }} />
                    </div>

                    <h3 style={{ 
                      fontSize: '1.125rem', 
                      fontWeight: '600', 
                      marginBottom: '0.75rem',
                      color: 'hsl(var(--foreground))'
                    }}>
                      {program.title}
                    </h3>

                    <p style={{ 
                      fontSize: '0.9375rem', 
                      color: 'hsl(var(--muted-foreground))',
                      marginBottom: '1rem',
                      lineHeight: 1.6
                    }}>
                      {program.description}
                    </p>

                    <div 
                      className="d-flex align-items-center gap-2 mb-3"
                      style={{
                        background: 'hsl(var(--secondary))',
                        padding: '0.5rem 0.75rem',
                        borderRadius: 'var(--radius)',
                        fontSize: '0.875rem',
                        width: 'fit-content'
                      }}
                    >
                      <Clock size={14} style={{ color: 'hsl(var(--primary))' }} />
                      <span>{language === 'hi' ? 'अवधि' : 'Duration'}: {program.duration}</span>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.5rem', flex: 1 }}>
                      {program.features.map((feature, fIndex) => (
                        <li 
                          key={fIndex}
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{ fontSize: '0.875rem' }}
                        >
                          <CheckCircle 
                            size={14} 
                            style={{ color: 'hsl(var(--success))', flexShrink: 0 }} 
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link 
                      to="/contact" 
                      className="btn btn-gov-outline btn-sm w-100 mt-auto"
                    >
                      {language === 'hi' ? 'आवेदन करें' : 'Apply Now'}
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* What We Offer */}
      <section className="gov-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <h2 className="gov-section-heading" style={{ textAlign: 'left' }}>
                {t('careers.benefits')}
              </h2>
              
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {benefits.map((benefit, index) => (
                  <li 
                    key={index}
                    className="d-flex align-items-center gap-3 mb-3"
                  >
                    <div 
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'hsl(var(--primary) / 0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <benefit.icon size={18} style={{ color: 'hsl(var(--primary))' }} />
                    </div>
                    <span style={{ fontWeight: '500' }}>{benefit.text}</span>
                  </li>
                ))}
              </ul>
            </Col>

            <Col lg={6}>
              <Card className="gov-card" style={{ borderLeft: '4px solid hsl(var(--accent))' }}>
                <Card.Body className="p-4">
                  <h3 style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: '600', 
                    marginBottom: '1rem',
                    color: 'hsl(var(--primary))'
                  }}>
                    {t('careers.applyTitle')}
                  </h3>
                  
                  <p style={{ 
                    color: 'hsl(var(--muted-foreground))',
                    marginBottom: '1.5rem',
                    lineHeight: 1.7
                  }}>
                    {t('careers.applyDesc')}
                  </p>

                  <div 
                    style={{
                      background: 'hsl(var(--secondary))',
                      borderRadius: 'var(--radius)',
                      padding: '1rem'
                    }}
                  >
                    <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      <strong>{language === 'hi' ? 'ईमेल' : 'Email'}:</strong>{' '}
                      <a href="mailto:info@igttech.com" style={{ color: 'hsl(var(--primary))' }}>
                        info@igttech.com
                      </a>
                    </p>
                    <p style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                      <strong>{language === 'hi' ? 'विषय' : 'Subject'}:</strong>{' '}
                      {language === 'hi' 
                        ? 'करियर आवेदन - [आपका नाम]' 
                        : 'Career Application - [Your Name]'}
                    </p>
                  </div>

                  <Link 
                    to="/contact" 
                    className="btn btn-gov-primary mt-4 d-inline-flex align-items-center gap-2"
                  >
                    {t('common.getInTouch')}
                    <ArrowRight size={18} />
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Areas of Work */}
      <section className="gov-section gov-section-alt">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {language === 'hi' ? 'कार्य क्षेत्र' : 'Areas of Work'}
            </h2>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              {language === 'hi' 
                ? 'इंटर्न और प्रशिक्षुओं के लिए उपलब्ध डोमेन' 
                : 'Available domains for interns and trainees'}
            </p>
          </div>

          <Row className="g-3 justify-content-center">
            {[
              language === 'hi' ? 'वेब डेवलपमेंट' : 'Web Development',
              language === 'hi' ? 'मोबाइल ऐप' : 'Mobile Apps',
              language === 'hi' ? 'आर्टिफिशियल इंटेलिजेंस' : 'Artificial Intelligence',
              language === 'hi' ? 'डेटा साइंस' : 'Data Science',
              language === 'hi' ? 'क्लाउड कंप्यूटिंग' : 'Cloud Computing',
              language === 'hi' ? 'DevOps' : 'DevOps',
              language === 'hi' ? 'डिजिटल मार्केटिंग' : 'Digital Marketing',
              language === 'hi' ? 'UI/UX डिज़ाइन' : 'UI/UX Design'
            ].map((area, index) => (
              <Col key={index} xs={6} sm={4} md={3}>
                <div 
                  style={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    padding: '1rem',
                    textAlign: 'center',
                    fontWeight: '500',
                    fontSize: '0.9375rem'
                  }}
                >
                  {area}
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Careers;
